import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Plus, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

const services = [
  {
    no: "001",
    title: "UI/UX Design",
    description:
      "Modern, responsive, and user-friendly websites designed to engage visitors and drive conversions.",
    tags: [
      "Landing Page",
      "Website Design",
      "Web App Design",
      "Wireframes",
      "Design System",
      "Prototype Design",
    ],
  },
  {
    no: "002",
    title: "UX Audits & Systems Framework",
    description:
      "End-to-end UX reviews that identify friction, prioritize wins, and produce a scalable design framework your team can actually use.",
    tags: ["Heuristic Review", "Usability Audit", "Component Library", "Token System"],
  },
  {
    no: "003",
    title: "AI & Web 3 / Blockchain Design",
    description:
      "Designing intuitive interfaces for AI-powered and decentralized products — bringing complex flows down to human scale.",
    tags: ["AI UX", "Wallet Flows", "Token Economies", "Onboarding"],
  },
  {
    no: "004",
    title: "Design Consultations",
    description:
      "1:1 sessions for founders, PMs and design leads. Strategy, hiring, design ops and product critique in a focused 60-minute block.",
    tags: ["Strategy", "Critique", "Hiring", "Process"],
  },
];

export default function Services() {
  return (
    <section
      data-testid="services-section"
      className="bg-zinc-950 text-white py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="flex items-start justify-between flex-wrap gap-6 mb-14"
        >
          <div className="flex items-end gap-4">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter">
              Services Offered
            </h2>
            <motion.span
              animate={{ rotate: [0, -10, 10, 0] }}
              transition={{ repeat: Infinity, duration: 4 }}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-500/20 text-brand-300 mb-2"
            >
              <Sparkles className="h-5 w-5" />
            </motion.span>
          </div>
          <a
            href="mailto:obiianayo@gmail.com"
            data-testid="services-lets-chat-btn"
            className="inline-flex items-center gap-2 text-sm font-medium bg-white text-zinc-950 hover:bg-brand-500 hover:text-white transition-colors px-5 py-2.5 rounded-full"
          >
            Let&apos;s have a chat
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-500"></span>
          </a>
        </motion.div>

        <Accordion
          type="single"
          collapsible
          defaultValue="item-001"
          className="border-t border-white/10"
          data-testid="services-accordion"
        >
          {services.map((s) => (
            <AccordionItem
              key={s.no}
              value={`item-${s.no}`}
              className="border-b border-white/10"
            >
              <AccordionTrigger
                data-testid={`service-trigger-${s.no}`}
                className="py-8 sm:py-10 hover:no-underline group"
              >
                <div className="flex items-baseline gap-6 sm:gap-10 flex-1 text-left">
                  <span className="text-zinc-500 text-sm tabular-nums">
                    ({s.no})
                  </span>
                  <span className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-white group-hover:text-brand-300 transition-colors">
                    {s.title}
                  </span>
                </div>
                <Plus className="h-5 w-5 shrink-0 text-zinc-400 transition-transform duration-300 group-data-[state=open]:rotate-45" />
              </AccordionTrigger>
              <AccordionContent className="pb-10 pl-0 sm:pl-20">
                <p className="text-zinc-400 max-w-2xl text-base sm:text-lg leading-relaxed">
                  {s.description}
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {s.tags.map((t) => (
                    <span
                      key={t}
                      data-testid={`service-tag-${t.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-xs sm:text-sm text-zinc-300 border border-white/15 hover:border-brand-400/60 hover:text-brand-200 transition-colors px-3 py-1.5 rounded-full"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      {/* Hide default chevron from shadcn — replaced with Plus icon */}
      <style>{`
        [data-testid^="service-trigger-"] svg.lucide-chevron-down { display: none; }
      `}</style>
    </section>
  );
}
